import React from "react";
import { CreateFunction, UpdateFunction } from "@hex-insights/app-modules";
import { ClassNameProps, Column, DistributiveOmit, Rule, StyleProps } from "@hex-insights/core";
import { FieldIf, Form, FormEditButtonPair, FormState, SubmitButton, useInternalField } from "@hex-insights/forms";
import { Icon } from "../../../Components/Icon";
import {
	Contract,
	PaymentLineItemCreateMutation,
	PaymentLineItemDetailQuery,
	PaymentLineItemUpdateMutation,
	Student,
} from "../../../Utilities";
import { PaymentLineItemFormState, PaymentLineItemFormValues } from "../../FormUtilities";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import {
	useCreateFormControl,
	useCreateFormSubmission,
	useDetailFormControl,
	useDetailFormSubmission,
	useReadOnlyForm,
} from "./hooks";

export {
	type UseCreateFormArgs,
	useCreateForm,
	type UseDetailFormArgs,
	useDetailForm,
	type UseReadOnlyFormArgs,
	useReadOnlyForm,
} from "./hooks";

export type ControlledCreateProps = Omit<
	CreateProps<PaymentLineItemCreateMutation["createPaymentLineItem"]>,
	"formState" | "create"
> & {
	formStateArgs?: PaymentLineItemFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the PaymentLineItem model using an internally managed form state.
 */
export function ControlledCreate({ formStateArgs, ...props }: ControlledCreateProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <Create formState={formState} create={create} {...props} />;
}

export type CreateProps<T> = {
	formState: FormState<PaymentLineItemFormValues.Create>;
	create: CreateFunction<PaymentLineItemFormValues.Create, T>;
	onSuccess: (formState: FormState<PaymentLineItemFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
	localCurrency?: string;
} & BaseFormProps<PaymentLineItemFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the PaymentLineItem model using the given form state.
 */
export function Create<T>({
	formState,
	create,
	onSuccess,
	formNameSuffix,
	localCurrency = "",
	...props
}: CreateProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "paymentLineItem.create" + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<CreateFields formState={formState} localCurrency={localCurrency} />

				<Form.ActionBar>
					<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
						Submit
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}

type CreateFieldsProps = {
	formState: FormState<PaymentLineItemFormValues.Create>;
	localCurrency: string;
};

function CreateFields({ formState, localCurrency }: CreateFieldsProps) {
	return (
		<React.Fragment>
			<Field.DisplayOrder formState={formState} />
			<Field.Amount formState={formState} />
			<Field.LocalAmount formState={formState} localCurrency={localCurrency} />
			<Field.Invoice.Select formState={formState} />
			<Field.Payment.Select formState={formState} />
		</React.Fragment>
	);
}

export type ControlledDetailProps = Omit<
	DetailProps<PaymentLineItemUpdateMutation["updatePaymentLineItem"]>,
	"formState" | "update"
> & {
	formStateArgs?: Partial<DistributiveOmit<PaymentLineItemFormState.UseDetailFormStateArgs, "initialFormValues">>;
};

/**
 * Detail form of the PaymentLineItem model using an internally managed form state.
 */
export function ControlledDetail({ formStateArgs, ...props }: ControlledDetailProps) {
	const { formState, update } = useDetailFormControl({ ...formStateArgs, paymentLineItem: props.paymentLineItem });

	return <Detail formState={formState} update={update} {...props} />;
}

export type DetailProps<T> = {
	paymentLineItem: PaymentLineItemDetailQuery["paymentLineItem"];
	formState: FormState<PaymentLineItemFormValues.Detail>;
	update: UpdateFunction<PaymentLineItemFormValues.Detail, T>;
	onSuccess: (formState: FormState<PaymentLineItemFormValues.Detail>, data: T) => void;
	formNameSuffix?: string;
	localCurrency?: string;
} & BaseFormProps<PaymentLineItemFormValues.Detail> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Detail form of the PaymentLineItem model using the given form state.
 */
export function Detail<T>({
	formState,
	paymentLineItem,
	update,
	onSuccess,
	formNameSuffix,
	localCurrency = "",
	...props
}: DetailProps<T>) {
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	const formName = `paymentLineItem.detail.${paymentLineItem.id}` + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<FormEditButtonPair formState={formState} />

				<DetailFields formState={formState} paymentLineItem={paymentLineItem} localCurrency={localCurrency} />
			</Form.Column>
		</Form>
	);
}

export type ControlledReadOnlyProps = Omit<ReadOnlyProps, "formState"> & {
	formStateArgs?: Partial<DistributiveOmit<PaymentLineItemFormState.UseReadOnlyFormStateArgs, "initialFormValues">>;
};

/**
 * Read-only detail form of the PaymentLineItem model using an internally managed form state.
 */
export function ControlledReadOnly({ formStateArgs, ...props }: ControlledReadOnlyProps) {
	const { formState } = useReadOnlyForm({ ...formStateArgs, paymentLineItem: props.paymentLineItem });

	return <ReadOnly formState={formState} {...props} />;
}

export type ReadOnlyProps = {
	paymentLineItem: PaymentLineItemDetailQuery["paymentLineItem"];
	formState: FormState<PaymentLineItemFormValues.Detail>;
	localCurrency: string;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Read-only detail form of the PaymentLineItem model using the given form state.
 */
export function ReadOnly({ formState, paymentLineItem, localCurrency, className, style }: ReadOnlyProps) {
	return (
		<div className={className} style={style}>
			<Form.Column>
				<DetailFields formState={formState} paymentLineItem={paymentLineItem} localCurrency={localCurrency} />
			</Form.Column>
		</div>
	);
}

type DetailFieldsProps = {
	paymentLineItem: PaymentLineItemDetailQuery["paymentLineItem"];
	formState: FormState<PaymentLineItemFormValues.Detail>;
	localCurrency: string;
};

function DetailFields({ formState, paymentLineItem, localCurrency }: DetailFieldsProps) {
	return (
		<React.Fragment>
			<Field.DisplayOrder formState={formState} />
			<Field.Amount formState={formState} />
			<Field.LocalAmount formState={formState} localCurrency={localCurrency} />
			<Field.Invoice.Select formState={formState} currentInstance={paymentLineItem.invoice} />
			<Field.Payment.Select formState={formState} currentInstance={paymentLineItem.payment} />
		</React.Fragment>
	);
}

export type CreateFieldsInPaymentProps = {
	formState: FormState<PaymentLineItemFormValues.CreateInPayment>;
	studentID?: Student["id"];
	contractID?: Contract["id"];
	isInLocalCurrency: boolean;
	localCurrency: string;
};

export function CreateFieldsInPayment({
	formState,
	studentID,
	contractID,
	isInLocalCurrency = false,
	localCurrency,
}: CreateFieldsInPaymentProps) {
	useInternalField(formState, "displayOrder");

	return (
		<React.Fragment>
			<Field.Invoice.Select formState={formState} noHeader studentID={studentID} contractID={contractID} />
			<Column justify="spaced-start" verticalSpacing="1rem">
				<FieldIf formState={formState} name="localAmount" condition={isInLocalCurrency}>
					<Field.LocalAmount
						formState={formState}
						noHeader
						disabled={!isInLocalCurrency}
						localCurrency={localCurrency}
					/>
					<Rule>
						<div style={{ backgroundColor: "var(--general---background-color)" }}>
							<Icon.Swap size={22} color="var(--primary-color)" className="block" />
						</div>
					</Rule>
				</FieldIf>
				<Field.Amount formState={formState} noHeader />
			</Column>
		</React.Fragment>
	);
}
